import { useState } from "react";
import { ArrowRight, Check, ChevronRight, Flag, GitMerge, Layers, Play, Plus } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@monorepo-apps/ui/src/components/input";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

import type { IterationView } from "@/application/use-cases/b2c/build-b2c-board";
import { isHttpUrl } from "@/application/use-cases/b2c/is-http-url";
import { ActionButton } from "@/presentation/components/delivery-tasks/action-button";
import { StatusSelect } from "@/presentation/components/board/status-select";
import { StatusDot } from "@/presentation/components/status/status-dot";
import { CopyCode } from "@/presentation/components/b2c/copy-code";
import { IterationBranches } from "@/presentation/components/b2c/iteration-branches";
import { MarathonProgress } from "@/presentation/components/b2c/marathon-progress";
import {
  ITERATION_STATUS_LABEL,
  ITERATION_STATUS_TONE,
  KIND_LABEL,
  PRIORITY_BADGE,
  PRIORITY_LABEL,
} from "@/presentation/data/b2c-board";
import type { B2cRepo, B2cTaskRow } from "@/infrastructure/supabase/b2c-board.types";
import type { DeliveryStatus } from "@/infrastructure/supabase/database.types";

export interface IterationActions {
  /** Start the marathon. Absent when the iteration may not start (gate closed, another running). */
  onStart: (() => void) | null;
  /** Close the marathon with its output document. */
  onFinish: (outputUrl: string | null) => void;
  onMerge: () => void;
  onAddTask: (title: string) => void;
  onTaskStatus: (task: B2cTaskRow, status: DeliveryStatus) => void;
  /** Move a task to the next iteration (created when missing). */
  onMoveToNext: (task: B2cTaskRow) => void;
  /** Open the task in the drawer. */
  onOpenTask: (task: B2cTaskRow) => void;
  /** Set or clear one repo's PR link (adds the repo's branch when missing). */
  onSetBranchPr: (repo: B2cRepo, url: string | null) => void;
  /** Add a repo's integration branch to the iteration. */
  onAddRepo: (repo: B2cRepo) => void;
  /** Fallback when a repo's merge workflow did not record the merge. */
  onMarkBranchMerged: (repo: B2cRepo) => void;
}

/**
 * One iteration of a workstream: its marathon state, branch, output document,
 * progress, and tasks. The actions walk the marathon model:
 * planned → marathon → feedback (output delivered) → merged.
 */
export function IterationBlock({
  view,
  canWrite,
  startBlockedReason,
  actions,
}: {
  view: IterationView;
  canWrite: boolean;
  /** Why the marathon cannot start yet, shown in place of the Start button. */
  startBlockedReason: string | null;
  actions: IterationActions;
}) {
  const { iteration, tasks, progress } = view;
  const [finishing, setFinishing] = useState(false);
  const [outputUrl, setOutputUrl] = useState(iteration.output_url ?? "");
  const [newTask, setNewTask] = useState("");
  // Merged iterations start collapsed; open ones start expanded.
  const [expanded, setExpanded] = useState(iteration.status !== "merged");
  const editable = canWrite && iteration.status !== "merged";

  const submitTask = () => {
    const title = newTask.trim();
    if (!title) return;
    actions.onAddTask(title);
    setNewTask("");
  };

  return (
    <div
      className={cn(
        "border-border flex flex-col gap-3 rounded-lg border p-3",
        iteration.status === "marathon" && "border-[oklch(0.74_0.16_80_/_0.55)]"
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? `Collapse iteration ${iteration.number}`
              : `Expand iteration ${iteration.number}`
          }
          className="text-muted-foreground hover:text-foreground -ml-1 flex items-center gap-1"
        >
          <ChevronRight className={cn("size-3.5 transition-transform", expanded && "rotate-90")} />
          <span className="font-data text-foreground text-[12px] font-semibold">
            Iteration {iteration.number}
          </span>
        </button>
        <span className="flex items-center gap-1.5 text-[12px]">
          <StatusDot
            tone={ITERATION_STATUS_TONE[iteration.status]}
            pulse={iteration.status === "marathon"}
          />
          {ITERATION_STATUS_LABEL[iteration.status]}
        </span>
        {view.stackedOn ? (
          <span
            className="font-data flex items-center gap-1 rounded-md bg-[oklch(0.72_0.17_190_/_0.08)] px-1.5 py-0.5 text-[11px] text-[oklch(0.45_0.13_195)]"
            title={`Iteration ${view.stackedOn.number} has not merged yet. Each repo's branch is cut from, and PRs into, the branch of the nearest earlier iteration that changed that repo, until it merges (see the repo chips).`}
          >
            <Layers className="size-3" aria-hidden />
            stacked on it. {view.stackedOn.number}
          </span>
        ) : null}
      </div>

      {view.branches.length > 0 || editable ? (
        <IterationBranches
          branches={view.branches}
          canWrite={editable}
          onSetPr={actions.onSetBranchPr}
          onAddRepo={actions.onAddRepo}
          onMarkMerged={actions.onMarkBranchMerged}
        />
      ) : null}

      <MarathonProgress progress={progress} status={iteration.status} />

      {expanded ? (
        <>
          {iteration.goal ? (
            <p className="text-muted-foreground text-[12px]">{iteration.goal}</p>
          ) : null}

          {canWrite ? (
            <div className="flex flex-wrap items-center gap-2">
              {iteration.status === "planned" ? (
                actions.onStart ? (
                  <ActionButton
                    label="Start marathon"
                    icon={Play}
                    surface="card"
                    onClick={actions.onStart}
                  />
                ) : (
                  <span className="text-muted-foreground text-[11px] italic">
                    {startBlockedReason}
                  </span>
                )
              ) : null}

              {iteration.status === "marathon" && !finishing ? (
                <ActionButton
                  label="Finish marathon"
                  icon={Flag}
                  surface="card"
                  onClick={() => setFinishing(true)}
                />
              ) : null}

              {iteration.status === "marathon" && finishing ? (
                <form
                  className="flex w-full flex-wrap items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const link = outputUrl.trim() || null;
                    if (link && !isHttpUrl(link)) {
                      toast.error("The output link must start with https://");
                      return;
                    }
                    actions.onFinish(link);
                    setFinishing(false);
                  }}
                >
                  <Input
                    value={outputUrl}
                    onChange={(e) => setOutputUrl(e.target.value)}
                    placeholder="Link to the marathon output document (optional)"
                    className="h-8 min-w-0 flex-1 text-[12px]"
                    aria-label="Marathon output document link"
                  />
                  <button
                    type="submit"
                    className="border-border hover:border-primary/50 hover:text-primary inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[11px] font-bold"
                  >
                    <Check className="size-3.5" aria-hidden />
                    Close marathon
                  </button>
                  <button
                    type="button"
                    onClick={() => setFinishing(false)}
                    className="text-muted-foreground px-2 text-[11px] hover:underline"
                  >
                    Cancel
                  </button>
                </form>
              ) : null}

              {iteration.status === "feedback" && view.mergeBlockedBy !== null ? (
                <span className="text-muted-foreground text-[11px] italic">
                  Merge iteration {view.mergeBlockedBy} first — this branch is stacked on it.
                </span>
              ) : null}
              {iteration.status === "feedback" &&
              view.mergeBlockedBy === null &&
              view.openRepos.length > 0 ? (
                <span className="text-muted-foreground text-[11px] italic">
                  Merges when {view.openRepos.join(", ")}{" "}
                  {view.openRepos.length > 1 ? "merge" : "merges"}.
                </span>
              ) : null}
              {iteration.status === "feedback" &&
              view.mergeBlockedBy === null &&
              view.openRepos.length === 0 ? (
                <ActionButton
                  label="Feedback done — merge"
                  icon={GitMerge}
                  surface="card"
                  onClick={actions.onMerge}
                />
              ) : null}
            </div>
          ) : null}

          <ul className="flex flex-col">
            {tasks.map((task) => (
              // The row is a mouse convenience; the title button is the keyboard and
              // screen-reader trigger, so no interactive control nests inside another.
              <li
                key={task.id}
                onClick={() => actions.onOpenTask(task)}
                className={cn(
                  "border-border/60 hover:bg-muted/50 -mx-1 flex cursor-pointer items-center gap-2 rounded-md border-t px-1 py-1.5 first:border-t-0",
                  task.is_e2e_proof && "bg-[oklch(0.72_0.17_190_/_0.06)]"
                )}
              >
                <CopyCode code={task.code} className="w-[74px]" />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    actions.onOpenTask(task);
                  }}
                  aria-label={`Open ${task.code}: ${task.title}`}
                  className="focus-visible:ring-primary/40 min-w-0 flex-1 rounded text-left text-[12.5px] leading-snug focus-visible:ring-2 focus-visible:outline-none"
                >
                  {task.is_e2e_proof ? (
                    <span className="text-primary font-data mr-1.5 text-[10px] font-bold tracking-[0.12em] uppercase">
                      E2E
                    </span>
                  ) : (
                    <span
                      className={cn(
                        "font-data mr-1.5 rounded px-1 py-px text-[9.5px] font-bold tracking-[0.1em]",
                        PRIORITY_BADGE[task.priority].className
                      )}
                      title={PRIORITY_LABEL[task.priority]}
                    >
                      {PRIORITY_BADGE[task.priority].text}
                    </span>
                  )}
                  {task.kind === "decision" || task.kind === "reconfigure" ? (
                    <span className="font-data text-muted-foreground mr-1.5 text-[10px] tracking-wide uppercase">
                      {KIND_LABEL[task.kind]}
                    </span>
                  ) : null}
                  {task.title}
                  {task.is_e2e_proof && task.description ? (
                    <span className="text-muted-foreground mt-0.5 block text-[11.5px]">
                      {task.description}
                    </span>
                  ) : null}
                </button>
                {/* Stop the click here: the select's menu is portalled, but React still
                bubbles its clicks through this row, which would open the drawer. */}
                <div
                  className="w-[150px] shrink-0 whitespace-nowrap"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <StatusSelect
                    value={task.status}
                    onChange={(s) => actions.onTaskStatus(task, s)}
                    variant="cell"
                    disabled={!canWrite}
                  />
                </div>
                {editable && !task.is_e2e_proof && task.status !== "done" ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      actions.onMoveToNext(task);
                    }}
                    title="Move to the next iteration"
                    aria-label={`Move ${task.code} to the next iteration`}
                    className="text-muted-foreground hover:text-primary shrink-0 p-1"
                  >
                    <ArrowRight className="size-3.5" aria-hidden />
                  </button>
                ) : (
                  <span className="w-[22px] shrink-0" aria-hidden />
                )}
              </li>
            ))}
            {tasks.length === 0 ? (
              <li className="text-muted-foreground py-2 text-[12px] italic">
                No tasks in this iteration.
              </li>
            ) : null}
          </ul>

          {editable ? (
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                submitTask();
              }}
            >
              <Input
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder={
                  iteration.status === "marathon"
                    ? "Add a task this marathon needs to finish its work"
                    : "Add a task to this iteration"
                }
                className="h-8 min-w-0 flex-1 text-[12px]"
                aria-label={`New task in iteration ${iteration.number}`}
              />
              <button
                type="submit"
                disabled={!newTask.trim()}
                className="border-border hover:border-primary/50 hover:text-primary inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[11px] font-bold disabled:opacity-40"
              >
                <Plus className="size-3.5" aria-hidden />
                Add
              </button>
            </form>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
